import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { User as UserIcon, MapPin, PawPrint, Package, AlertCircle, CheckCircle2 } from 'lucide-react';

import { TopBar, Btn, Pill, Avatar, Kbd, Letter, AtomBubble, AtomSays, RosterRail, StageLayout, Wheel, Confetti, GameTopBar, TallyCascade, KeepTableCard, LadderBanner, LadderRunOver, useToast, TALLY, STAGE, TV_PACE } from '../../components/wr';
import { ShareSheet } from '../../components/wr/ShareSheet';
import { useGame, useWebSocket } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useKeyboardSurface, useMinWidth, useSurface } from '../../hooks/useSurface';
import { blockedCategories, NOT_FOR_THIS_TABLE } from '../../util/blockedWords';
import { composePrefixed, composeWhole, startsWithRoundLetter } from './raceField';
import { soundService } from '../../services/soundService';
import { useTvConnected } from '../../hooks/useTvConnected';
import { websocketService } from '../../services/websocketService';
import type { AtomSaysPayload, BanList, LadderRun, LadderRung } from '../../services/websocketService';
import type { RoundEndedPayload, WebSocketEvents } from '../../services/websocketService';

/** Everything that can drag a user back into a room they've left. */
function clearRoomStorage() {
  for (const k of ['currentRoomCode', 'lastGameActivity', 'wr.gameState']) {
    try { localStorage.removeItem(k); } catch {}
  }
}

/**
 * The five beats of a round, in order:
 *
 *   spin     → the letter
 *   race     → 60 seconds of typing
 *   pencils  → "Pencils down." (1.2s — the collective exhale)
 *   tally    → THE CASCADE. This is the product.
 *   final    → the winner
 *
 * `tally` is where the laughter happens (`vision.md`), so it is not
 * optional, not host-only, and not behind a flag. It runs on every phone.
 */
type Phase = 'spin' | 'race' | 'pencils' | 'tally' | 'final';

interface GameResultPlayer {
  playerId: string;
  playerName: string;
  playerAvatar: string;
  totalScore: number;
  position: number;
  badges?: string[];
  firebaseUserId?: string | null;
}

/**
 * Backend `gameEnded` payload — note the field name. The server sends
 * `players: [...]` (the ranked list), NOT `results`. The earlier mismatch
 * is what caused the "winner with 0 points" bug: FinalPhase was reading
 * `data.results`, finding nothing, and falling back to `state.players`
 * whose `score` field was never updated from server totals.
 */
interface GameEndedPayload {
  finalResults?: any;
  /** Ranked list, sorted desc by totalScore. */
  players?: GameResultPlayer[];
  /** Legacy alias — some older paths still use this name. */
  results?: GameResultPlayer[];
  winner?: GameResultPlayer | { id: string; name: string; avatar: string };
  rounds?: any[];
  gameMode?: string;
  totalRounds?: number;
}

const CATEGORY_ICON_MAP: Record<string, React.FC<{ size?: number; color?: string }>> = {
  Name: ({ size = 16, color }) => <UserIcon size={size} color={color} />,
  Place: ({ size = 16, color }) => <MapPin size={size} color={color} />,
  Animal: ({ size = 16, color }) => <PawPrint size={size} color={color} />,
  Thing: ({ size = 16, color }) => <Package size={size} color={color} />,
};

interface Squeeze {
  byName: string;
  secondsLeft: number;
}

/**
 * The game-screen orchestrator. Consumes websocket events and drives the
 * round loop. Gameplay data comes from GameContext + sockets; the phase
 * machine is local.
 */
export default function GameScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { state, dispatch } = useGame();
  const { submitAnswers, leaveRoom } = useWebSocket();
  const { roomCode: roomCodeParam } = useParams<{ roomCode?: string }>();
  const roomCode = roomCodeParam || state.roomCode || '';

  const [phase, setPhase] = useState<Phase>(() => {
    if (state.currentScreen === 'results' || state.finalResults) return 'final';
    return 'spin';
  });
  const [roundEnded, setRoundEnded] = useState<RoundEndedPayload | null>(null);
  const [finalResults, setFinalResults] = useState<GameEndedPayload | null>(null);
  /** The id the share card lives at. Arrives on `shareReady`, just after the game ends. */
  const [shareGameId, setShareGameId] = useState<string | null>(null);
  const [submittedPlayers, setSubmittedPlayers] = useState<Set<string>>(new Set());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [squeeze, setSqueeze] = useState<Squeeze | null>(null);
  /**
   * Everything Atom has said this round, keyed by the moment it said it.
   *
   * THIS LIVES HERE, AND NOT IN <AtomSays>, ON PURPOSE. GameScreen is mounted
   * for the whole game; the phase components are not. The first version had
   * <AtomSays> subscribe to the socket on mount, and it missed every single
   * line — the round-start line is emitted the instant the round starts, and
   * SpinPhase does not render the bubble until the wheel has finished spinning
   * 1.5s later. It was subscribing to an event that had already happened.
   * A component that mounts late cannot be the one holding the subscription.
   */
  const [atomSays, setAtomSays] = useState<Partial<Record<AtomSaysPayload['occasion'], AtomSaysPayload>>>({});
  /** The leave / abort sheet. Reachable from EVERY phase — see `exitDoor`. */
  const [leaveOpen, setLeaveOpen] = useState(false);

  /**
   * THE LADDER.
   *
   * `rung` is which step you are standing on (and, from the tally, whether you
   * survived it). `bans` are the words that are off the board this round. Both
   * arrive on `round_started`, together with the letter, because a ban that shows
   * up after you have started typing is a trap rather than a rule.
   *
   * All three are null in every other mode, and every line below that touches them
   * is guarded — Classic renders exactly as it did.
   */
  const [rung, setRung] = useState<LadderRung | null>(null);
  const [bans, setBans] = useState<BanList | null>(null);
  const [ladderRun, setLadderRun] = useState<LadderRun | null>(null);

  // Socket handlers are bound once, so they can't read `phase`/`finalResults`
  // from the closure. Mirror into refs.
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const finalResultsRef = useRef<GameEndedPayload | null>(finalResults);
  useEffect(() => { finalResultsRef.current = finalResults; }, [finalResults]);

  // If the GameContext already has finalResults (e.g., we landed on
  // /game-results after refresh), seed local state so FinalPhase renders
  // immediately and we don't fall through to the spin placeholder.
  useEffect(() => {
    if (state.finalResults && !finalResults) {
      setFinalResults(state.finalResults);
      setPhase('final');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.finalResults]);

  // Cold-land protection: someone hits /game-results or /game/CODE without
  // any local state (refreshed an old link, opened a stale tab). After a
  // brief grace period (so the socket can rehydrate via gameStateRestore),
  // kick them back to /home rather than leave them staring at "preparing
  // the round" forever.
  useEffect(() => {
    if (state.finalResults || finalResults) return; // active results view
    if (state.currentLetter && phase !== 'spin') return; // active gameplay
    const t = setTimeout(() => {
      const stillEmpty = !state.currentLetter && !state.finalResults && !finalResults && !state.roomCode;
      if (stillEmpty) {
        console.log('🚪 GameScreen cold-landed without state — routing home.');
        navigate('/home', { replace: true });
      }
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentLetter, state.finalResults, state.roomCode]);

  /**
   * WALKING IN THROUGH THE DOOR, RATHER THAN BEING CARRIED IN BY THE GAME.
   *
   * Every existing route into this screen arrives with the game already in
   * context: you were in the lobby, the host pressed start, and GameContext was
   * populated on the way. Async has no such path. Mom taps "your turn" on the
   * home screen — or the card in the table thread — eight hours after her seat was
   * created, with a cold React tree and nothing in context but a room code in the
   * URL. Without this she hits the cold-land guard above and is bounced to /home,
   * which is a spectacular way to lose the one feature the mode exists for.
   *
   * `getGameState` is the whole answer: the server finds any session she holds a
   * seat in (she was seated when the round was left for her), joins her socket to
   * the room, and sends back the letter, the roster, her empty sheet — and the
   * tally she slept through. It is also what makes a refresh mid-turn boring,
   * which is the other half of the requirement.
   */
  useEffect(() => {
    if (!roomCodeParam) return;
    if (state.roomCode === roomCodeParam && state.currentLetter) return;
    websocketService.requestGameState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCodeParam, state.roomCode]);

  /**
   * IS THERE A TELEVISION IN THIS ROOM?
   *
   * When there is, this phone stops being the stage and becomes a CONTROLLER.
   * The race strips to four inputs and a button; the tally moves to the wall,
   * where everyone can groan at the same collision at the same moment. The host
   * keeps the controls, because somebody has to be able to settle an argument.
   *
   * It flips back the instant the TV is switched off, mid-phase, with no reload
   * and no lost round.
   */
  const tvConnected = useTvConnected(roomCode);

  const players = state.players;
  const me = state.currentPlayer;
  const isHost = state.isHost;
  const categories = state.gameSettings.categories || ['Name', 'Place', 'Animal', 'Thing'];
  const totalRounds = state.totalRounds || state.gameSettings.rounds || 5;
  const currentRound = state.currentRound;
  const letter = state.currentLetter;
  const timeRemaining = state.timeRemaining;

  /**
   * Letters so far — the round history, for the desktop race rail. The phone
   * has nowhere to put this (and doesn't miss it); a laptop has a whole empty
   * column to the left of the stage, and "we've had B, K, and now N" is
   * exactly the kind of thing you'd know if you were looking at a sheet of
   * paper on a table.
   *
   * Server state has no round history, so we accumulate it — indexed by round
   * so a reconnect mid-game doesn't double-count.
   */
  const [lettersByRound, setLettersByRound] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!letter || !currentRound) return;
    setLettersByRound(prev => (prev[currentRound] === letter ? prev : { ...prev, [currentRound]: letter }));
  }, [letter, currentRound]);
  const lettersSoFar = useMemo(
    () => Object.keys(lettersByRound)
      .map(Number)
      .sort((a, b) => a - b)
      .map(r => ({ round: r, ch: lettersByRound[r] })),
    [lettersByRound],
  );

  /* ---------- WebSocket wiring ----------
   * Subscribe to the CANONICAL event name only. The service emits exactly
   * one name per event; the old code registered both the snake_case and
   * camelCase spellings for every event, which meant any alias the service
   * did emit (game_ended / gameEnded) fired the handler twice.
   */
  useEffect(() => {
    const resetRound = () => {
      setRoundEnded(null);
      setSubmittedPlayers(new Set());
      setAnswers({});
      setSqueeze(null);
      setAtomSays({});
    };

    /**
     * ⚠️ BLOCKER (backend, not ours): the server force-starts the next round
     * on a hardcoded 8s timer after `roundEnded`
     * (`backend/src/gameLogic.js` — `this.nextRoundTimer = setTimeout(…, 8000)`).
     * Measured live: the tally gets 8,019 ms. The cascade needs ~26s at two
     * players and ~45s at four.
     *
     * So when `round_started` lands mid-cascade we ABORT the tally and go to
     * the next round. That is the correct call — the server's round clock is
     * already running and stealing race time from the player would be worse
     * than cutting the reveal short — but it does mean the tally is truncated
     * in a real game until that timer is removed or made host-gated. The
     * comment beside it ("the tally screen takes ~3s") describes the old
     * four-tap tally, not this one.
     */
    const onRoundStarted = (data: WebSocketEvents['round_started']) => {
      resetRound();
      // The rung and its ban list, in the same breath as the letter.
      setRung(data?.ladder || null);
      setBans(data?.bans || null);
      setPhase('spin');
    };

    const onPlayerSubmitted = (data: { playerId: string }) => {
      setSubmittedPlayers(prev => {
        const next = new Set(prev);
        next.add(data.playerId);
        return next;
      });
    };

    /**
     * The squeeze. The first player hit Done, so the round now ends in 10
     * seconds for everyone — enough to finish the word you're typing, not
     * enough to think of a new one. GameContext snaps the clock; we just
     * name the culprit.
     */
    const onTimeSqueeze = (data: Squeeze) => {
      setSqueeze({ byName: data?.byName || 'Someone', secondsLeft: data?.secondsLeft ?? 10 });
    };

    /**
     * Atom said something.
     *
     * Stashed by occasion and read by whichever phase is on screen when the
     * player gets there — which is emphatically NOT the phase that was on screen
     * when the event arrived. The tally line lands on `roundEnded`, while the
     * player is still watching `pencils`; the round-start line lands before the
     * wheel has stopped. Holding them here is what makes both of them survive
     * the gap.
     *
     * The squeeze line is the exception: it goes into the race's EXISTING status
     * lane rather than a new bubble, because that lane is a fixed-height row
     * built so that nothing moves under the player's thumb while they are
     * typing. A speech bubble appearing below four inputs mid-word is the exact
     * layout shift that lane exists to prevent.
     */
    const onAtomSays = (data: WebSocketEvents['atom_says']) => {
      if (!data?.text || !data.occasion) return;
      setAtomSays(prev => ({ ...prev, [data.occasion]: data }));
    };

    const onRoundEnded = (data: RoundEndedPayload) => {
      setRoundEnded(data);
      // The rung, now judged: `verdict` tells you whether you are still alive.
      if (data.ladder) setRung(data.ladder);
      // Pencils down first. Then the cascade.
      setPhase('pencils');
      if (data.playerTotalScores) {
        dispatch({ type: 'UPDATE_TOTAL_SCORES', payload: data.playerTotalScores });
      }
    };

    /**
     * On the FINAL round the server emits `gameEnded` immediately after
     * `roundEnded` (gameLogic: endGame() is called synchronously). If we
     * jumped straight to the winner screen we'd skip the last round's tally
     * entirely — the most dramatic one. Stash the results and let the
     * cascade finish; it hands over when it's done.
     */
    const onGameEnded = (data: GameEndedPayload) => {
      setFinalResults(data);
      // The run, over. Stashed like the rest of the results and shown after the
      // cascade — the tally that killed you is the one you most want to watch.
      if ((data as any)?.ladder) setLadderRun((data as any).ladder as LadderRun);
      const p = phaseRef.current;
      if (p !== 'pencils' && p !== 'tally') setPhase('final');
    };

    /**
     * The game record has landed, so the share card exists. The results screen
     * is already up; this is what lights the share sheet under it.
     */
    const onShareReady = (data: WebSocketEvents['shareReady']) => {
      if (data?.gameId) setShareGameId(data.gameId);
    };

    const onGameStarted = (data: any) => {
      resetRound();
      setFinalResults(null);
      setShareGameId(null);
      // A rematch of a run is a NEW run, from rung one. There is no other thing
      // "play again" could mean.
      setLadderRun(null);
      setRung(null);
      setBans(null);
      setPhase('spin');
      if (typeof data?.totalRounds === 'number' && data.totalRounds > 0) {
        dispatch({ type: 'UPDATE_GAME_SETTINGS', payload: { rounds: data.totalRounds } });
      }
    };

    /**
     * The host settled a word by hand. The server has rescored and told
     * EVERYONE — so patch the round payload the cascade is rendering from and
     * the chip re-flips on this phone too, host or not.
     */
    const onScoreAdjusted = (data: WebSocketEvents['score_adjusted']) => {
      if (!data?.playerId || !data?.category) return;
      setRoundEnded(prev => {
        if (!prev) return prev;
        const details = prev.answerDetails || {};
        const forPlayer = details[data.playerId] || {};
        const cell = forPlayer[data.category];
        if (!cell) return prev;
        // Prefer the SERVER's rescored cell — it comes back with
        // `status: 'override'`, which is what stops the word rendering as
        // struck-through-and-dimmed while proudly showing a green 10.
        const next = data.cell
          ? { ...cell, ...data.cell, overridden: true }
          : { ...cell, points: data.points, status: 'override' as const, overridden: true };
        return {
          ...prev,
          scores: data.scores || prev.scores,
          playerTotalScores: data.playerTotalScores || prev.playerTotalScores,
          answerDetails: {
            ...details,
            [data.playerId]: { ...forPlayer, [data.category]: next },
          },
        };
      });

      // ✓ Mor'du has been added to MOVIE NIGHT.
      //
      // A nine-year-old knew something the machine did not. Her grandmother backed
      // her, and the game remembered — forever. Say it once, quietly, and get out
      // of the way: the tally is the product, and this is not allowed to become
      // chrome on it. (Null for most overrides — settling a misspelling teaches the
      // pack nothing — and always null in Classic, which has no key to teach.)
      if (data.learned) {
        toast.success(`“${data.learned.word}” has been added to ${data.learned.packName}.`);
      }

      if (data.playerTotalScores) {
        dispatch({ type: 'UPDATE_TOTAL_SCORES', payload: data.playerTotalScores });
      }
    };

    /**
     * The host aborted before the game really got going. Nothing is recorded —
     * no win, no loss. Everyone just goes home.
     */
    const onGameAborted = () => {
      clearRoomStorage();
      dispatch({ type: 'SET_GAME_RESULTS', payload: null });
      dispatch({ type: 'SET_SCREEN', payload: 'home' });
      dispatch({ type: 'LEAVE_ROOM' });
      navigate('/home', { replace: true });
    };

    websocketService.on('round_started', onRoundStarted);
    websocketService.on('answer_submitted', onPlayerSubmitted);
    websocketService.on('time_squeeze', onTimeSqueeze);
    websocketService.on('atom_says', onAtomSays);
    websocketService.on('round_ended', onRoundEnded);
    websocketService.on('game_ended', onGameEnded);
    websocketService.on('game_started', onGameStarted);
    websocketService.on('score_adjusted', onScoreAdjusted);
    websocketService.on('game_aborted', onGameAborted);
    websocketService.on('shareReady', onShareReady);
    return () => {
      websocketService.off('round_started', onRoundStarted);
      websocketService.off('answer_submitted', onPlayerSubmitted);
      websocketService.off('time_squeeze', onTimeSqueeze);
      websocketService.off('atom_says', onAtomSays);
      websocketService.off('shareReady', onShareReady);
      websocketService.off('round_ended', onRoundEnded);
      websocketService.off('game_ended', onGameEnded);
      websocketService.off('game_started', onGameStarted);
      websocketService.off('score_adjusted', onScoreAdjusted);
      websocketService.off('game_aborted', onGameAborted);
    };
  }, [dispatch, navigate]);

  /* ---------- Does a TV have the room? ----------
   * THE ONE-ROOM RULE. Six phones firing the same sound 40ms apart is chaos,
   * so when a TV observer is watching, the phones hand the SHARED sounds (the
   * spin, the letter, the tick, the pops, the chord) over to it — the sound
   * should come from where the eyes are.
   *
   * The `observer_joined` / `observer_left` broadcast and the `tvConnected`
   * room-state flag belong to the cast-to-TV work, which is landing on another
   * branch. This consumes all three shapes it could arrive in and defaults to
   * FALSE, which degrades to "no TV" — phones keep their sounds. The fallback
   * is the status quo, never silence.
   *
   * The `as any` is deliberate: `observer_*` is not in the WebSocketEvents map
   * yet, and adding it here would collide with the branch that owns that file.
   */
  useEffect(() => {
    const ws = websocketService as any;

    // An explicit count wins; otherwise the event name IS the state.
    const observed = (data: any, joined: boolean) => {
      if (typeof data?.observerCount === 'number') return data.observerCount > 0;
      if (typeof data?.tvConnected === 'boolean') return data.tvConnected;
      return joined;
    };
    const onJoin = (d: any) => soundService.setTvConnected(observed(d, true));
    const onLeave = (d: any) => soundService.setTvConnected(observed(d, false));
    // A phone that JOINS a room a TV is already watching never sees the
    // `observer_joined` it missed — so also read the flag off room state.
    const onState = (d: any) => {
      if (typeof d?.tvConnected === 'boolean') soundService.setTvConnected(d.tvConnected);
      else if (typeof d?.observerCount === 'number') soundService.setTvConnected(d.observerCount > 0);
    };

    try {
      ws.on?.('observer_joined', onJoin);
      ws.on?.('observer_left', onLeave);
      ws.on?.('gameState', onState);
      ws.on?.('room_joined', onState);
      ws.on?.('players_updated', onState);
    } catch { /* the events don't exist yet — we are the ones who are early */ }

    return () => {
      try {
        ws.off?.('observer_joined', onJoin);
        ws.off?.('observer_left', onLeave);
        ws.off?.('gameState', onState);
        ws.off?.('room_joined', onState);
        ws.off?.('players_updated', onState);
      } catch { /* nothing to unbind */ }
      // Leaving the room means leaving the TV behind.
      soundService.setTvConnected(false);
    };
  }, []);

  /**
   * WHO HAS ALREADY GONE — from the SERVER'S roster, not from events we missed.
   *
   * `submittedPlayers` is built by listening to `answer_submitted` as it happens.
   * That works perfectly in a live game, where everyone is in the room for the
   * whole sixty seconds. It is empty on arrival in an async game, where the
   * events that would have filled it were broadcast eight hours ago to a room
   * this client was not connected to.
   *
   * Two consequences, both bad, both fixed by reading the roster the server sends
   * on every broadcast:
   *
   *   1. Mom opens her turn and the sheet says "0/2 done" — when Arslan's whole
   *      point in leaving it was that he had played. The one fact she most needs
   *      is the one thing the screen was denying.
   *   2. Worse: Mom submits, closes the tab, comes back — and `meSubmitted` is
   *      false, so she is handed a blank sheet for a round she has already played
   *      and invited to write it again.
   *
   * Additive only: a round start clears the Set (resetRound) — and, since the
   * mid-round-lockout fix, START_ROUND also zeroes `hasSubmitted` across
   * state.players, mirroring the server's own per-round reset. That second half
   * was missing, and this effect is exactly why it mattered: a roster snapshot
   * taken while people had submitted (a phone locking mid-tally is enough)
   * survived the round boundary, and the first re-mapped players array of the
   * new round fed the stale ticks back in here — locking a player's sheet for
   * a round they had not played. See the START_ROUND reducer for the autopsy.
   */
  useEffect(() => {
    const alreadyIn = players.filter(p => p.hasSubmitted).map(p => p.id);
    if (alreadyIn.length === 0) return;
    setSubmittedPlayers(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of alreadyIn) if (!next.has(id)) { next.add(id); changed = true; }
      return changed ? next : prev;
    });
  }, [players]);

  /* ---------- Spin auto-advances to race after 2.5s ---------- */
  useEffect(() => {
    if (phase !== 'spin' || !letter) return;
    if (finalResults || state.finalResults) return;
    const t = setTimeout(() => setPhase('race'), 2500);
    return () => clearTimeout(t);
  }, [phase, letter, finalResults, state.finalResults]);

  /* ---------- "Pencils down." — 1.2s, then the cascade ---------- */
  useEffect(() => {
    if (phase !== 'pencils') return;
    const t = setTimeout(() => setPhase('tally'), TALLY.PENCILS_MS);
    return () => clearTimeout(t);
  }, [phase]);

  /* ---------- Local timer countdown ----------
   * Display only. The SERVER owns round end — this used to auto-submit the
   * player the instant its own local clock hit zero, which in multiplayer
   * could happen before the server's round timer had even started.
   */
  const isAsync = !!state.gameSettings?.isAsync;
  useEffect(() => {
    if (isAsync) return;
    if (phase !== 'race' || timeRemaining <= 0) return;
    const t = setTimeout(() => {
      dispatch({ type: 'UPDATE_TIMER', payload: Math.max(0, timeRemaining - 1) });
    }, 1000);
    return () => clearTimeout(t);
  }, [phase, timeRemaining, dispatch, isAsync]);

  /* ---------- The last five seconds ----------
   * A soft woodblock, once a second, for the last FIVE — not the last ten.
   * Ten ticks is a nag; five is a pulse you feel in your shoulders. It fires
   * off the same displayed countdown the players are reading, so the sound and
   * the number can never disagree.
   *
   * SHARED: with a TV in the room the phones go quiet and the TV keeps time,
   * which is the correct instinct anyway — a clock belongs on the wall.
   */
  useEffect(() => {
    if (isAsync || phase !== 'race') return;
    if (timeRemaining > 5 || timeRemaining <= 0) return;
    soundService.tick();
  }, [timeRemaining, phase, isAsync]);

  /* ---------- The way out ----------
   * Every phase gets an X, and the X always does something. The playtest bug
   * was that the leave affordance vanished the moment anybody submitted — so
   * a player who wanted out had to kill the tab.
   *
   * ABORT is only honest while the game hasn't really begun: round 1, nobody
   * has submitted anything, no round has been scored. After that the game has
   * a history, and leaving is a FORFEIT — say so plainly.
   */
  const nothingPlayedYet =
    currentRound <= 1 && submittedPlayers.size === 0 && !roundEnded && !finalResults;
  const canAbort = isHost && nothingPlayedYet;

  const goHome = async ({ abort }: { abort: boolean }) => {
    setLeaveOpen(false);
    clearRoomStorage();
    // Dispatch BEFORE navigating — without these, App.tsx's effect sees
    // currentScreen === 'game' + a roomCode and navigates RIGHT BACK to
    // /game/CODE, which is its own little trap.
    dispatch({ type: 'SET_SCREEN', payload: 'home' });
    dispatch({ type: 'SET_GAME_RESULTS', payload: null });
    dispatch({ type: 'LEAVE_ROOM' });
    try {
      // Abort ends it for the whole room and records nothing. Forfeit is just
      // me leaving; the others play on.
      if (abort) websocketService.abortGame();
      await leaveRoom();
    } catch (e) {
      console.error('leave failed — going home anyway', e);
    }
    navigate('/home', { replace: true });
  };

  const exitDoor = (
    <LeaveConfirmModal
      open={leaveOpen}
      canAbort={canAbort}
      onCancel={() => setLeaveOpen(false)}
      onLeave={() => goHome({ abort: canAbort })}
    />
  );

  /** Host settled a word by hand → tell the server; it rescores and tells everyone. */
  const handleOverride = (playerId: string, category: string, points: number) => {
    websocketService.overrideScore(playerId, category, points);
  };

  /* ---------- The Ladder ----------
   * A run has no length — it lasts exactly as long as you keep beating Atom — so
   * the round pill drops its denominator, and the rung banner carries the letter's
   * ban list and the name of the Atom you are about to face. */
  const isLadder = !!rung;
  const roundsShown = isLadder ? 0 : totalRounds;
  const ladderBanner = rung ? <LadderBanner rung={rung} bans={bans} /> : null;

  /* ---------- Phase render ---------- */
  if (phase === 'spin') {
    return (
      <>
        <SpinPhase
          round={currentRound}
          totalRounds={roundsShown}
          letter={letter}
          atomLine={atomSays.round_start || null}
          banner={ladderBanner}
          onLeave={() => setLeaveOpen(true)}
        />
        {exitDoor}
      </>
    );
  }

  if (phase === 'race' || phase === 'pencils') {
    return (
      <>
        <RacePhase
          round={currentRound}
          totalRounds={roundsShown}
          banner={rung ? <LadderBanner rung={rung} bans={bans} compact /> : null}
          letter={letter}
          categories={categories}
          timeRemaining={timeRemaining}
          isAsync={isAsync}
          players={players}
          me={me}
          roomCode={roomCode}
          submittedPlayers={submittedPlayers}
          squeeze={squeeze}
          atomLane={atomSays.squeeze?.text || null}
          pencilsDown={phase === 'pencils'}
          controller={tvConnected}
          lettersSoFar={lettersSoFar}
          answers={answers}
          setAnswers={setAnswers}
          submitting={submitting}
          onSubmit={async final => {
            if (submitting || state.isSubmitted) return;
            setSubmitting(true);
            try {
              await submitAnswers(final);
            } catch (e) {
              console.error(e);
            } finally {
              setSubmitting(false);
            }
          }}
          onLeave={() => setLeaveOpen(true)}
        />
        {exitDoor}
      </>
    );
  }

  if (phase === 'tally' && roundEnded) {
    // A REPLAY is judged by the round it is showing, not by the round the game has
    // since moved on to. Resuming an async game on round 5 and replaying round 4's
    // cascade would otherwise dress it up as the finale — "last round" pacing and
    // all — for a round that is followed by another one.
    //
    // And a LADDER run has no last round at all: it ends when you lose, not when
    // you reach N. `!isLadder` has to survive the replay branch, or every round of
    // a run gets the finale's pacing.
    const isLastRound = roundEnded.isReplay
      ? roundEnded.roundNumber >= totalRounds
      : (!!finalResults || (!isLadder && currentRound >= totalRounds));
    return (
      <>
        <div className="app-shell">
          {/* `wr-page--stage` (1280) rather than the default 720 content
              column — TallyCascade puts a StageLayout inside, and the grid
              needs the room. On a phone the class does nothing at all. */}
          <div className="screen-pad wr-page wr-page--stage" style={{ position: 'relative' }}>
            <GameTopBar
              round={currentRound}
              totalRounds={roundsShown}
              centerLabel={
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: tvConnected ? 'var(--accent)' : 'var(--ink-3)' }}>
                  {tvConnected ? (isHost ? 'THE REMOTE' : 'ON THE TV') : 'THE TALLY'}
                </span>
              }
              onClose={() => setLeaveOpen(true)}
            />

            {/*
              THE VERDICT, IN THE SAME BREATH AS THE CHIPS.
              You find out whether you survived while you are still looking at the
              words that decided it. Not on a screen afterwards — here, over the
              tally, which is where the argument is.
            */}
            {rung?.verdict && (
              <div
                className="wr-row gap-2"
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 12,
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: rung.verdict === 'advance' ? 'var(--green, #1f8f4a)' : 'var(--coral, #b94a3b)',
                  background: rung.verdict === 'advance'
                    ? 'color-mix(in srgb, var(--green, #1f8f4a) 10%, transparent)'
                    : 'color-mix(in srgb, var(--coral, #b94a3b) 10%, transparent)',
                }}
              >
                {rung.verdict === 'advance'
                  ? `YOU ${rung.you} · ATOM ${rung.atom} — YOU CLIMB`
                  : `YOU ${rung.you} · ATOM ${rung.atom} — THE RUN ENDS HERE`}
              </div>
            )}
            {/*
              THE CASCADE IS ALWAYS MOUNTED — even for a non-host with a TV in
              the room, whose screen is covered by the "Look up 📺" panel.
              That is deliberate, and it is what makes the TV hot-unpluggable:
              the timeline is a deterministic function of the same `roundEnded`
              payload on every client, so it is already running at the correct
              beat underneath. Pull the TV's power out mid-tally and the panel
              lifts to reveal a cascade that is exactly where the television
              was — not one that restarts from the top.

              And when a TV IS connected, every phone runs at the television's
              pace too. Slow only the TV and the host's phone would finish the
              cascade first and call the next round, cutting the show off on the
              wall mid-laugh.
            */}
            <TallyCascade
              letter={roundEnded.letter || letter}
              categories={categories}
              roundData={roundEnded}
              players={players}
              isHost={isHost}
              isLastRound={isLastRound}
              paceScale={tvConnected ? TV_PACE : 1}
              onOverride={handleOverride}
              onComplete={() => {
                // The last round's results were stashed while the cascade
                // played. Hand over to the winner screen now.
                if (finalResultsRef.current) {
                  setPhase('final');
                  return;
                }
                /*
                  A REPLAYED TALLY MUST NOT MOVE THE GAME.

                  In an async game the round that just finished playing on this
                  screen finished on the SERVER hours ago — we are watching a
                  recording of a round the server has already advanced past. The
                  live path ends a cascade by asking for the next round; doing that
                  here would tell the server to skip the round we are about to walk
                  into, and the host is exactly the person most likely to be the one
                  catching up. So: ack it, and land in the round already in flight.
                */
                if (roundEnded?.isReplay) {
                  websocketService.tallySeen(roundEnded.roundNumber);
                  setRoundEnded(null);
                  setPhase('spin');
                  return;
                }
                // Ask the server to start the next round immediately rather
                // than waiting out its safety timer.
                if (isHost) websocketService.nextRound();
                setPhase('spin');
              }}
            />

            {/*
              ATOM ON THE TALLY. This is the one that matters.

              The cascade is where the laughter is — chips flip 10 → 5, three
              people wrote Cat, somebody's Xerus survives — and until now nothing
              in the room acknowledged any of it. Atom's line lands here or it
              lands nowhere.

              It sits BELOW the cascade deliberately: the words are the show, and
              Atom is the person at the table saying "three of you wrote Cat"
              while everyone is still looking at the grid. It also arrives late by
              design (the server gives a model a few seconds of a 20–45s window),
              which lands it right about when the reveal is sinking in — and if it
              never arrives, nothing here moves.
            */}
            <AtomSays line={atomSays.tally || null} className="wr-col" />

            {/* Everyone who is not holding the remote: look up. */}
            {tvConnected && !isHost && (
              <LookUpPanel
                categories={categories}
                answers={roundEnded.submissions?.[me?.id || ''] || answers}
              />
            )}
          </div>
        </div>
        {exitDoor}
      </>
    );
  }

  /**
   * ⚠️ The `&& finalResults` guard used to live on this branch, and it is what
   * TRAPPED the playtesters at game end.
   *
   * `phase` initialises to 'final' whenever `state.currentScreen === 'results'`
   * — which RESTORE_GAME_STATE also sets, from the server's `status:
   * 'completed'`, WITHOUT ever populating `state.finalResults`. So on any
   * reconnect / refresh / rehydrate at game end you got phase 'final' with a
   * null payload, fell straight through to the "Setting up the round…"
   * placeholder, and sat there forever while the nav cheerfully read
   * "Game over". There was no way out.
   *
   * FinalPhase already ranks `state.players` by score when the payload has no
   * ranking, so render it regardless and let it degrade. A slightly thin
   * results screen beats a dead end every single time.
   */
  /**
   * THE RUN IS OVER.
   *
   * A run does not get a leaderboard, a winner podium or a share card — it gets a
   * number. `LadderRunOver` is that number, the climb that produced it, and a
   * button that says "Go again", which is the only thing the player wants at this
   * moment and the only metric this feature has.
   *
   * "Go again" starts a FRESH RUN rather than dropping into a lobby: a rematch of
   * a run is a new run, from rung one, and there is nothing to configure.
   */
  if (phase === 'final' && ladderRun) {
    return (
      <LadderRunOver
        run={ladderRun}
        onPlayAgain={() => {
          void Promise.resolve().then(() => leaveRoom()).catch(() => {});
          dispatch({ type: 'SET_GAME_RESULTS', payload: null });
          dispatch({ type: 'LEAVE_ROOM' });
          clearRoomStorage();
          navigate('/solo', { replace: true, state: { startRun: true } });
        }}
        onHome={() => {
          void Promise.resolve().then(() => leaveRoom()).catch(() => {});
          dispatch({ type: 'SET_SCREEN', payload: 'home' });
          dispatch({ type: 'SET_GAME_RESULTS', payload: null });
          dispatch({ type: 'LEAVE_ROOM' });
          clearRoomStorage();
          navigate('/home', { replace: true });
        }}
      />
    );
  }

  if (phase === 'final') {
    return (
      <FinalPhase
        finalResults={finalResults || {}}
        players={players}
        totalRounds={totalRounds}
        shareGameId={shareGameId}
        onPlayAgain={() => {
          dispatch({ type: 'SET_SCREEN', payload: 'lobby' });
          dispatch({ type: 'SET_GAME_RESULTS', payload: null });
          setFinalResults(null);
          navigate(`/room/${roomCode}`, { replace: true });
        }}
        onHome={() => {
          // TELL THE SERVER, not just ourselves.
          //
          // This used to dispatch LEAVE_ROOM — a local reducer action — and clear
          // localStorage, and stop. The socket was never told. So the server went
          // on believing you were sitting in a finished game, and the next time
          // you reloaded it restored you back into it: the route guard saw an
          // active room, bounced you to the results you had already left, and
          // /leaderboard, /friends and /tables became unreachable until the room
          // finally died on its own. That is the "stuck after game over, couldn't
          // get home" bug, and it is also why a player's own game history could
          // look wrong — they never actually left.
          //
          // Fire-and-forget: the walk home must not wait on a socket, and it must
          // not fail if the socket is already gone (leaveRoom throws with no
          // current player). Leaving is the one action that has to work every time.
          void Promise.resolve()
            .then(() => leaveRoom())
            .catch(() => { /* already disconnected — the server will reap us */ });

          dispatch({ type: 'SET_SCREEN', payload: 'home' });
          dispatch({ type: 'SET_GAME_RESULTS', payload: null });
          dispatch({ type: 'LEAVE_ROOM' });
          setFinalResults(null);
          clearRoomStorage();
          navigate('/home', { replace: true });
        }}
        onSignIn={() => navigate('/login')}
      />
    );
  }

  /**
   * The last-resort placeholder. It is reachable (phase 'tally' before
   * `roundEnded` lands, a torn socket mid-round), so it must NEVER be a dead
   * end: it always offers a door. The version that shipped to the playtest was
   * a bare line of text with no way off it.
   */
  return (
    <>
      <div className="app-shell">
        <div className="screen-pad">
          <GameTopBar
            round={currentRound}
            totalRounds={totalRounds}
            onClose={() => setLeaveOpen(true)}
          />
          <div className="wr-col wr-flex-1 wr-center gap-4" style={{ padding: 32, textAlign: 'center' }}>
            <div className="wr-text-3">Setting up the round…</div>
            <Btn kind="ghost" icon="House" onClick={() => goHome({ abort: false })}>
              Back to home
            </Btn>
          </div>
        </div>
      </div>
      {exitDoor}
    </>
  );
}

/* ============================================================ */
/* PHASE 1 — Letter spin                                         */
/* ============================================================ */

function SpinPhase({
  round,
  totalRounds,
  letter,
  atomLine,
  banner,
  onLeave,
}: {
  round: number;
  totalRounds: number;
  letter: string;
  /** Atom calling the letter, in its own voice. Null in a room with no Atom. */
  atomLine?: AtomSaysPayload | null;
  /** The Ladder's rung + ban list. Null in every other mode. */
  banner?: React.ReactNode;
  onLeave?: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!letter) { setRevealed(false); return; }
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), 1500);
    return () => clearTimeout(t);
  }, [letter]);

  // No letter yet → between-rounds, server is preparing the next one.
  if (!letter) {
    return (
      <div className="app-shell">
        <div className="screen-pad">
          <GameTopBar round={round} totalRounds={totalRounds} onClose={onLeave} />
          <div className="wr-col wr-flex-1 wr-center gap-4">
            <div className="eyebrow accent">PREPARING NEXT ROUND</div>
            <div
              style={{
                width: 100, height: 100, borderRadius: 28,
                background: 'linear-gradient(135deg, var(--accent-2), var(--accent))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'wr-pulse-glow 2s ease-in-out infinite',
              }}
            >
              <div className="wr-skel" style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(255,255,255,0.3)' }} />
            </div>
            <AtomBubble msg="Shuffling the wheel…" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="screen-pad">
        <GameTopBar round={round} totalRounds={totalRounds} onClose={onLeave} />
        {/* The rung and its ban, ABOVE the wheel — so the ban is the second thing
            you read after the letter, and the first thing you read before typing. */}
        {banner}
        <div className="wr-col wr-flex-1 wr-center gap-4">
          <div className="eyebrow accent">{revealed ? 'GO!' : 'SPINNING…'}</div>
          <Wheel size={280} selected={letter} spinning={!revealed} />
          {revealed && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/*
                Atom calls the letter — in ITS voice, which follows from the
                difficulty you picked. The Coach says "Letter K. Take your time";
                the Rival says "K. Clock's running." Same letter, different
                opponent, and you can tell which one you chose without reading a
                label.

                `fallback` is the line this screen has hardcoded since the day it
                was written. It still shows when there is no Atom at the table at
                all — a four-human room has nobody to do the talking.
              */}
              <AtomSays
                line={atomLine || null}
                fallback={<AtomBubble msg={`Letter ${letter} — Names, Places, Animals, Things. Go!`} kind="celebration" />}
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* PHASE 2 — Race                                                */
/* ============================================================ */

interface RacePhaseProps {
  round: number;
  totalRounds: number;
  /**
   * The Ladder's rung + ban list, compact. It stays on screen FOR THE WHOLE RACE,
   * not just the reveal: nobody remembers a ban they read four seconds ago while a
   * clock was running, and a ban you have to remember is a punishment for having a
   * bad memory rather than a rule about words.
   */
  banner?: React.ReactNode;
  letter: string;
  categories: string[];
  timeRemaining: number;
  /** Async / correspondence mode — no countdown urgency, no auto-end. */
  isAsync?: boolean;
  players: any[];
  me: any;
  /** Passed to RosterRail, which needs it to make rows open Block / Report. */
  roomCode?: string;
  submittedPlayers: Set<string>;
  squeeze: Squeeze | null;
  /** Atom's reaction to the squeeze. Replaces the generic lane text when set —
   *  same row, same height, a voice instead of a template. */
  atomLane?: string | null;
  /** The 1.2s beat between the round ending and the tally starting. */
  pencilsDown: boolean;
  /**
   * A TELEVISION IS IN THE ROOM, so this phone is a controller.
   *
   * The letter, the clock, the squeeze and the table are all on the wall now,
   * at a size the whole family can read at once. Reproducing them here would be
   * asking everyone to choose which screen to look at — and they would choose
   * the one in their hand, which is the exact failure TV mode exists to prevent.
   *
   * So the phone becomes utilitarian on purpose: four inputs and a button.
   */
  controller?: boolean;
  /** Round history for the desktop left rail. Empty on a phone. */
  lettersSoFar?: Array<{ round: number; ch: string }>;
  answers: Record<string, string>;
  setAnswers: (a: Record<string, string>) => void;
  submitting: boolean;
  onSubmit: (final: Record<string, string>) => Promise<void>;
  onLeave: () => void;
}

/** The status lane is a FIXED 24px tall. Nothing in it may change layout. */
const STATUS_LANE_H = 24;
/** Long enough not to spam the socket per keystroke, short enough to beat the buzzer. */
const DRAFT_DEBOUNCE_MS = 500;

function RacePhase({
  round, totalRounds, banner, letter, categories, timeRemaining, isAsync, players, me, roomCode,
  submittedPlayers, squeeze, atomLane, pencilsDown, controller = false, lettersSoFar = [], answers, setAnswers, submitting,
  onSubmit, onLeave,
}: RacePhaseProps) {
  const meSubmitted = me ? submittedPlayers.has(me.id) : false;
  const urgent = !isAsync && timeRemaining <= 10 && timeRemaining > 0;
  const filledCount = categories.filter(c => (answers[c] || '').trim().length > 0).length;
  const canSubmit = !meSubmitted && !submitting && filledCount > 0 && !pencilsDown;

  /**
   * ✋ "THAT'S A REAL WORD — JUST NOT ONE FOR THIS TABLE."
   *
   * The referee refuses these and scores them 0. Before this, the player found
   * that out by LOSING THE ROUND with the word still sitting in the box — which
   * reads as a bug, not a rule, because nothing ever told them otherwise.
   *
   * So the field says it as they type. Recomputed every keystroke (a Set lookup
   * on four strings — it costs nothing), so the amber arrives on the character
   * that completes the word and leaves on the character that fixes it.
   *
   * The list is GENERATED from the server's own screen and a test asserts the
   * two agree on all 154k corpus words, so the field cannot promise something
   * the referee won't honour. See src/util/blockedWords.ts.
   */
  const blocked = useMemo(
    () => blockedCategories(answers, categories),
    [answers, categories]
  );
  const blockedSet = useMemo(() => new Set(blocked), [blocked]);

  /**
   * "Type the whole word" — the Settings toggle. No coral prefix; the player
   * types "Eel" from the E. A per-PHONE display preference and never a lobby
   * rule: it changes nothing about scoring, only what this one screen prints
   * in the box. Read once per race mount — there is a spin between rounds, so
   * a change in Settings lands cleanly at the next letter.
   */
  const [wholeWord] = useState<boolean>(() => {
    try { return localStorage.getItem('wr.type-whole-word') === 'on'; } catch { return false; }
  });

  /**
   * Whole-word mode's counterpart to the amber above: a real word that just
   * doesn't start with the round's letter. Prefix mode cannot produce this —
   * the prefix manufactures compliance — but with the prefix gone the server
   * would quietly score it 0, so the field says it in the same amber, while
   * there is still time to change it.
   */
  const wrongStart = useMemo(
    () => (wholeWord ? categories.filter(c => !startsWithRoundLetter(letter, answers[c] || '')) : []),
    [wholeWord, categories, answers, letter]
  );
  const wrongStartSet = useMemo(() => new Set(wrongStart), [wrongStart]);

  const surface = useSurface();
  // With a TV in the room the phone has no stage to lay out — it has a form.
  // Every desktop flourish (the rails, the ambient timer bar, the big letter)
  // is a second stage competing with the first one, so none of them run.
  const desktop = surface === 'desktop' && !controller;
  /** ≥1280 — the only width at which the LEFT rail earns its keep. */
  const wide = useMinWidth(1280);
  /** Desktop layout AND a real pointer. The whole keyboard act hangs off this. */
  const kb = useKeyboardSurface();

  /* ==================================================================== */
  /* THE KEYBOARD CHOREOGRAPHY — the reason a laptop beats a phone here.  */
  /*                                                                      */
  /* Hands never leave the home row:                                      */
  /*                                                                      */
  /*   1. Round starts → the FIRST FIELD IS ALREADY FOCUSED. The letter   */
  /*      is already the prefix, so you just type `arah`.                 */
  /*   2. Enter → jumps to the next EMPTY field, not the next sequential  */
  /*      one. Skipping the fields you've already filled is the entire    */
  /*      speed win; a strict tab order makes you walk past your own      */
  /*      work. Wraps around.                                             */
  /*   3. Tab / Shift+Tab → strict sequential. That's the native          */
  /*      behaviour and we do not fight it — a user who reaches for Tab   */
  /*      wants the boring thing. ↑/↓ also move between fields.           */
  /*   4. All four filled → Enter ARMS the Done button: focus moves to    */
  /*      it, it takes the ring, and the label becomes "Press Enter to    */
  /*      lock in". A SECOND Enter submits. Two steps, on purpose: in a   */
  /*      squeeze the whole table's round is riding on your last ten      */
  /*      seconds, and an accidental single-keystroke lock-in would be    */
  /*      unforgivable.                                                   */
  /*   5. Esc does NOTHING. Deliberately. Leaving mid-round is a decision */
  /*      that should cost you a deliberate mouse trip to the X.          */
  /* ==================================================================== */
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const doneRef = useRef<HTMLButtonElement | null>(null);
  const [armed, setArmed] = useState(false);

  const allFilled = filledCount === categories.length && categories.length > 0;
  const inputsLocked = meSubmitted || pencilsDown;

  // Beat 1 — autofocus the first field the frame the race mounts. Never on a
  // phone: focusing an input there throws the software keyboard up over half
  // the screen before the player has even read the letter.
  useEffect(() => {
    if (!kb || inputsLocked) return;
    inputRefs.current[0]?.focus();
    // Mount only — refocusing on every answer change would fight the typist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kb]);

  // Disarm the moment the paper stops being complete (you deleted a word).
  useEffect(() => {
    if (!allFilled && armed) setArmed(false);
  }, [allFilled, armed]);

  const focusField = useCallback((i: number) => {
    const el = inputRefs.current[i];
    if (!el) return;
    el.focus();
    // Caret to the end — the prefix means you're always appending.
    const len = el.value.length;
    try { el.setSelectionRange(len, len); } catch { /* number inputs etc. */ }
  }, []);

  /**
   * DONE — but not with that word still in the box.
   *
   * A blocked answer must never be silently submitted to score 0; that is the
   * whole bug. So Done doesn't fire: it sends them back to the offending field
   * with the caret in it, ready to be changed. The field is already amber and
   * the note already says why, so this is a nudge to the right box, not a new
   * message.
   *
   * The one choke point for BOTH the mouse and the keyboard's arm-then-Enter
   * path, because both end up here.
   *
   * The clock keeps running. That is fair — and if it runs out, the round ends
   * normally and the server scores the word 0, exactly as it does today. The
   * player is never worse off than before; they just get a chance to fix it.
   */
  const handleDone = useCallback(() => {
    // A wrong-start word gets the identical treatment: the server scores both
    // 0, so both get fixed (or cleared) before Done means anything.
    const offending = blocked.length > 0 ? blocked[0] : wrongStart[0];
    if (offending) {
      const i = categories.indexOf(offending);
      if (i >= 0) focusField(i);
      return;
    }
    void onSubmit(answers);
  }, [blocked, wrongStart, categories, focusField, onSubmit, answers]);

  const onFieldKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!kb) return;
    const n = categories.length;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (allFilled) {
        // Beat 4 — arm, don't fire.
        setArmed(true);
        doneRef.current?.focus();
        return;
      }
      // Beat 2 — next EMPTY field, wrapping. Never the next sequential one.
      for (let k = 1; k <= n; k++) {
        const j = (i + k) % n;
        if (!(answers[categories[j]] || '').trim()) {
          focusField(j);
          return;
        }
      }
      return;
    }

    // Beat 3 — ↑/↓ move between fields. Tab is left entirely alone.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusField((i + 1) % n);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusField((i - 1 + n) % n);
    }
  };

  /**
   * ⏱️ WHAT YOU TYPED IS WHAT YOU GET.
   *
   * The worst bug of the playtest: the clock ran out, and everything you'd
   * typed but not "submitted" was thrown away and scored as blanks. People had
   * written four words and got nothing.
   *
   * So we stream the draft to the server as you type. It stores it against the
   * player and grades it at timeout, so a round that ends on the buzzer scores
   * exactly what was on screen. Debounced (not per-keystroke), and only ever
   * our OWN answers — the server keeps drafts private until the round ends, or
   * we'd be broadcasting everyone's words live and the game would be pointless.
   */
  useEffect(() => {
    if (meSubmitted || pencilsDown) return;
    const t = setTimeout(() => {
      websocketService.sendDraftAnswers(answers);
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [answers, meSubmitted, pencilsDown]);

  const handleChange = (cat: string, typed: string, pasted = false) => {
    if (meSubmitted || pencilsDown) return;
    // A pencil scratch under your own typing. PERSONAL — it fires from this
    // device's keystrokes only, so it can never chorus around the room.
    // Throttled to 1 per 300ms PER FIELD (the bucket key is the category), so
    // a fast typist gets a scratch and not a hiss, and typing in Name doesn't
    // mute typing in Place.
    soundService.scribble(cat);
    // The keystroke logic lives in raceField.ts as a pure function, because
    // the two inline versions before it each ate real words at a real table
    // ("Ooze" the first time, "Eel" the second) and neither could be tested
    // without a browser. The short version: what you type is what you get —
    // the old strip-a-leading-letter guess is gone, save for the one
    // unambiguous case (a DOUBLED leading letter can only be a re-typed
    // prefix). The full argument is on the function.
    const finalValue = wholeWord
      ? composeWhole(typed)
      : composePrefixed(letter, typed, { pasted });
    setAnswers({ ...answers, [cat]: finalValue });
  };

  // NOTE: there is deliberately NO auto-submit at time-zero. The client used
  // to fire one off its own free-running countdown — which in multiplayer can
  // reach zero before the SERVER's round timer has even started, submitting a
  // half-typed answer set on the player's behalf. The server owns round end.

  /**
   * The status lane. Everything that used to shove the inputs down mid-typing
   * — the "🏁 X is done" banner, the urgent Atom bubble — lives here now, in
   * a fixed-height row that cross-fades its contents. Zero layout shift, at
   * the exact moment layout shift is most expensive: while you're typing.
   */
  const firstDone = players.find(p => submittedPlayers.has(p.id) && p.id !== me?.id);
  let laneKey = 'idle';
  let laneText = '';
  let laneTone: 'accent' | 'muted' = 'muted';
  if (squeeze) {
    laneKey = 'squeeze';
    // Atom's line if it has one, the template if it hasn't. Identical row,
    // identical height — the only thing that changes is whether the sentence
    // came from a character or a format string.
    //
    // The squeeze does NOT go quiet because you're the one who caused it:
    // "the others have ten seconds" is exactly what a locked-in player is
    // waiting to know, and it used to vanish behind their own lock.
    laneText = meSubmitted
      ? `🏁 Last call — ${squeeze.secondsLeft} seconds for the rest of the table.`
      : atomLane || `🏁 ${squeeze.byName}'s done — ${squeeze.secondsLeft} seconds.`;
    laneTone = 'accent';
  } else if (urgent && !meSubmitted) {
    laneKey = 'urgent';
    laneText = 'Ten seconds.';
    laneTone = 'accent';
  } else if (firstDone && !meSubmitted) {
    laneKey = `done-${firstDone.id}`;
    laneText = `🏁 ${firstDone.name} is done`;
    laneTone = 'accent';
  }
  // No 'locked' line here any more — the LockedInSheet says it, larger and in
  // green, where the fields used to be. A muted lane on top of that would be
  // the same sentence twice.

  /* ---- The ambient timer -------------------------------------------------
     On a laptop the clock stops being a number you READ and becomes a bar you
     FEEL: 3px, full width, draining left→right, with a quiet mono numeral off
     in the left rail. The phone's timer chip is unchanged; at 1024–1279,
     where the left rail has been dropped, the numeral folds back into the top
     bar — which is exactly where the phone keeps it. */
  const roundLength = 60; // display-only; the SERVER owns round end.
  const pct = isAsync ? 100 : Math.max(0, Math.min(100, (timeRemaining / roundLength) * 100));
  const showTimerBar = desktop && !isAsync && !controller;
  const showRailClock = desktop && wide && !isAsync && !controller;

  const mm = Math.floor(Math.max(0, timeRemaining) / 60);
  const ss = (Math.max(0, timeRemaining) % 60).toString().padStart(2, '0');

  const leftRail = (
    <div className="wr-col gap-5">
      {!isAsync && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Time</div>
          <div className={`wr-race-clock ${urgent ? 'urgent' : ''}`.trim()}>
            {mm}:{ss}
          </div>
        </div>
      )}
      {lettersSoFar.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Letters</div>
          <div className="wr-letterchips">
            {lettersSoFar.map(l => (
              <span key={l.round} className={l.round === round ? 'now' : ''} title={`Round ${l.round}`}>
                {l.ch}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  /* ---- The table ---------------------------------------------------------
     EXACTLY what the phone's sub-strip shows: submitted-or-not, and the
     public running totals. Plus your OWN fill dots, which are your paper.
     Opponents' fill counts are NEVER here — see RosterRail's header. */
  const rightRail = (
    <RosterRail
      players={players}
      meId={me?.id}
      submitted={submittedPlayers}
      showTotals
      title="The table"
      youFilled={filledCount}
      youTotal={categories.length}
      // Makes the rows tappable → profile sheet → Block / Report. This is the
      // ONLY reach a player has to a stranger while the game is running.
      roomCode={roomCode}
    />
  );

  return (
    /* `wr-no-lift` — no hover-lift anywhere during the race. A card that
       jumps under the cursor while you're racing a clock is an insult. */
    <div className="app-shell wr-no-lift">
      <div className={`screen-pad ${desktop ? 'wr-page wr-page--stage' : ''}`.trim()}>
        <GameTopBar
          round={round}
          totalRounds={totalRounds}
          // Desktop ≥1280 hands the clock to the left rail; below that it
          // stays in the top bar, exactly as the phone has it. In CONTROLLER
          // mode the clock is on the wall, at 110px — putting a second one in
          // the corner of your hand just splits the room's attention.
          timeRemaining={isAsync || showRailClock || controller ? null : timeRemaining}
          centerLabel={controller ? (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--accent)' }}>
              📺 ON THE TV
            </span>
          ) : isAsync ? (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--ink-3)' }}>
              ASYNC · TAKE YOUR TIME
            </span>
          ) : undefined}
          onClose={onLeave}
        />

        {showTimerBar && (
          <div
            className={`wr-timerbar ${urgent ? 'urgent' : ''}`.trim()}
            role="timer"
            aria-label={`${timeRemaining} seconds left`}
          >
            <i style={{ width: `${pct}%` }} />
          </div>
        )}

        {/* The ban list stays up for the whole sixty seconds. See `banner` above. */}
        {banner && <div style={{ marginBottom: 8 }}>{banner}</div>}

        <StageLayout
          left={!controller && (showRailClock || lettersSoFar.length > 0) ? leftRail : undefined}
          right={controller ? undefined : rightRail}
        >
        {/* Fixed-height status lane — never reflows the column. Silent in
            controller mode: the squeeze, the finished-count and the countdown
            are all on the television, where the whole room reads them at once. */}
        <div
          style={{
            height: controller ? 0 : STATUS_LANE_H,
            position: 'relative',
            margin: controller ? 0 : '6px 0 2px',
            flexShrink: 0,
            overflow: 'hidden',
          }}
          aria-live="polite"
        >
          <AnimatePresence mode="wait">
            {laneText && (
              <motion.div
                key={laneKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--display)',
                  fontWeight: 700,
                  fontSize: 13,
                  lineHeight: 1,
                  color: laneTone === 'accent' ? 'var(--accent)' : 'var(--ink-3)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {laneText}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* The letter. Top-centre on every surface — that eye-line continuity
            is what makes the `layoutId` handoff from the settled wheel land as
            one continuous move instead of a cut. It grows on a desktop (the
            glyph goes 68 → 112, per STAGE.LETTER_RACE_LG); it does not move.

            In CONTROLLER mode it is gone entirely. The letter is 40vh tall on
            the wall, and it is already printed as the coral prefix inside all
            four fields — a third copy, in your hand, is just noise. */}
        {!controller && (
          <div className="wr-row wr-center mt-2">
            {desktop ? (
              <Letter
                ch={letter}
                size={STAGE.LETTER_RACE_LG}
                hero
                style={{ width: 152, height: 152, borderRadius: 34 }}
              />
            ) : (
              <Letter ch={letter} size={68} hero style={{ width: 96, height: 96, borderRadius: 24 }} />
            )}
          </div>
        )}

        {meSubmitted ? (
          /* Locked is not disabled — see LockedInSheet. */
          <LockedInSheet
            categories={categories}
            answers={answers}
            players={players}
            submittedPlayers={submittedPlayers}
            meId={me?.id}
          />
        ) : (
        <div className="wr-col gap-3 mt-4" style={{ flex: 1 }}>
          {categories.map((cat, i) => {
            const Icon = CATEGORY_ICON_MAP[cat] || (() => null);
            const value = answers[cat] || '';
            // In whole-word mode the box holds the word entire; with the
            // prefix, it holds the remainder and the coral letter is the rest.
            const shown = wholeWord
              ? value
              : value && value.toUpperCase().startsWith(letter.toUpperCase())
                ? value.slice(letter.length)
                : value;
            /* Two ambers, one visual language: a word this table refuses, and
               (whole-word mode only) a word that doesn't start with the
               round's letter. Both score 0 server-side; both get told here. */
            const isBlocked = blockedSet.has(cat);
            const isAmber = isBlocked || wrongStartSet.has(cat);
            return (
              <div key={cat}>
                <label
                  className="wr-row gap-2"
                  htmlFor={`wr-field-${cat}`}
                  /* The label goes amber with its field. Four boxes and one
                     shared note underneath: the colour is what says WHICH. */
                  style={{ marginBottom: 7, paddingLeft: 6, color: isAmber ? 'var(--amber)' : 'var(--ink-3)' }}
                >
                  <Icon size={12} />
                  <span className="eyebrow">{cat}</span>
                </label>
                {/* The shared `.field` rule is `align-items: baseline` + a flat
                    `padding: 0 16px`, which is what made the glyphs hug the top
                    of a tall box and sit tight against the edges. Both are
                    overridden here rather than in the stylesheet because
                    `.field` is shared with screens another agent owns. */}
                <div
                  className={`field ${isAmber ? 'is-blocked' : ''}`.trim()}
                  style={{
                    alignItems: 'center',
                    height: 'auto',
                    minHeight: 64,
                    padding: '14px 18px',
                    gap: 1,
                  }}
                >
                  {!wholeWord && <span className="prefix" style={{ lineHeight: 1.2 }}>{letter}</span>}
                  <input
                    id={`wr-field-${cat}`}
                    ref={el => { inputRefs.current[i] = el; }}
                    className="field-input"
                    style={{ lineHeight: 1.2, padding: 0 }}
                    value={shown}
                    /* Paste gets its own rule in raceField.ts (a paste is a
                       whole word; keystrokes are not), so the handler needs to
                       know which one this was. */
                    onChange={e =>
                      handleChange(cat, e.target.value, (e.nativeEvent as InputEvent).inputType === 'insertFromPaste')
                    }
                    onKeyDown={e => onFieldKeyDown(i, e)}
                    placeholder={`${cat.toLowerCase()}…`}
                    disabled={inputsLocked}
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    /* NOT "words". iOS capitalises the first character the user
                       types, which lands right after the coral prefix letter →
                       "OSCar". The prefix is the capital; the rest is typed
                       as-is. (Whole-word mode capitalises its own first letter
                       in composeWhole, deterministically, so it opts out of
                       iOS's guesswork too.) */
                    autoCapitalize="none"
                    aria-invalid={isAmber || undefined}
                    aria-describedby={isAmber ? 'wr-blocked-note' : undefined}
                  />
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* The note. Fixed height and always in the layout — see `.wr-field-note`.
            It is deliberately ONE line for the whole sheet rather than one per
            field: the amber box says which word, this says why, and a note that
            appears and disappears under individual inputs would shove the other
            three around while the player is mid-word. Blocked wins the line —
            it is the harsher rule — and the wrong-start note only exists in
            whole-word mode, where the prefix isn't there to make it moot. */}
        {!meSubmitted && (
        <div className="wr-field-note" id="wr-blocked-note" role="status" aria-live="polite">
          {blocked.length > 0 ? (
            <>
              <AlertCircle size={13} aria-hidden />
              <span>{NOT_FOR_THIS_TABLE}.</span>
            </>
          ) : wrongStart.length > 0 ? (
            <>
              <AlertCircle size={13} aria-hidden />
              <span>Every word this round starts with {letter}.</span>
            </>
          ) : null}
        </div>
        )}

        {/* Dev-only AI autofill.
            This shipped to EVERY player in EVERY production round: the render
            condition was just `!meSubmitted` — no env gate, no host check —
            despite a comment claiming it was "only shown to playtest hosts".
            `import.meta.env.DEV` is statically false in a production build, so
            Vite dead-code-eliminates the whole thing from the bundle. */}
        {import.meta.env.DEV && !inputsLocked && (
          <DevAutofillButton
            letter={letter}
            answers={answers}
            setAnswers={setAnswers}
          />
        )}

        {/* The sub-strip is the PHONE's table. On a desktop the same
            information — and only the same information — is the roster rail
            on the right, so showing both would be showing it twice. With a TV
            in the room it is on the wall, with 44px checkmarks. */}
        {!desktop && !controller && (
          <div className="sub-strip mt-3">
            <span className="label">{submittedPlayers.size} / {players.length} done</span>
            <div className="avs">
              {players.slice(0, 8).map(p => (
                <Avatar
                  key={p.id}
                  id={p.avatar}
                  size={28}
                  submitted={submittedPlayers.has(p.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* The button leaves with the fields. A grey disabled "You're locked
            in" bar under grey disabled inputs is what made the whole screen
            read as broken — the LockedInSheet's green check is the button's
            after-state now, and dead chrome doesn't get to keep the space. */}
        {!meSubmitted && (
        <Btn
          ref={doneRef}
          kind={canSubmit ? 'primary' : 'dark'}
          size="lg"
          full
          icon="Check"
          loading={submitting}
          disabled={!canSubmit}
          /* A paper flip, not a tap: this is the sound of your answers leaving
             your hands, and it is PERSONAL — it fires on YOUR lock-in and
             never on anybody else's, which is what stops five phones slapping
             paper at each other during a squeeze. (Passing it here also stops
             Btn adding its own `tap` on top.) */
          sound="lockin"
          onClick={handleDone}
          onBlur={() => setArmed(false)}
          className={armed ? 'wr-armed' : ''}
          style={{ marginTop: 12 }}
        >
          {armed ? (
            /* Beat 4, second half. The button is focused and ringed; the very
               next Enter is the native button activation. Say so in words —
               "I'M DONE" plus a ring is not an instruction. */
            <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
              <Kbd onAccent>⏎</Kbd> Press Enter to lock in
            </span>
          ) : (
            "I'M DONE"
          )}
        </Btn>
        )}

        {isAsync && meSubmitted && (
          <div
            className="card"
            style={{
              marginTop: 10,
              padding: 12,
              background: 'var(--bg-2)',
              boxShadow: 'none',
              fontSize: 12,
              color: 'var(--ink-3)',
              textAlign: 'center',
              lineHeight: 1.45,
            }}
          >
            {/*
              This used to say "You'll get a notification when the round is ready."
              There are no notifications in this product — no push, no email, nothing
              — so that sentence was a promise the software could not keep, made to
              someone who was about to close the tab and trust it. Say what actually
              happens instead: it waits for them at the table, and on the home screen.
            */}
            Waiting for {players.length - submittedPlayers.size} other {players.length - submittedPlayers.size === 1 ? 'player' : 'players'}.
            The result lands in your table's thread, and this game sits at the top of
            your home screen until it's your turn again. Safe to close this tab.
          </div>
        )}
        </StageLayout>
      </div>

      {/* "Pencils down." The collective exhale. 1.2s, 20% dim, inputs locked,
          one line. Do not decorate it. */}
      <AnimatePresence>
        {pencilsDown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 180,
              background: 'rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06, duration: 0.24 }}
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 800,
                fontSize: 30,
                letterSpacing: '-0.02em',
                color: '#fff',
                textShadow: '0 2px 18px rgba(0,0,0,0.35)',
              }}
            >
              Pencils down.
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================ */
/* LOCKED IN — done, not disabled                                */
/* ============================================================ */

/**
 * What the sheet becomes after YOUR Done.
 *
 * It used to stay four disabled grey inputs over a dead grey button, and on a
 * phone the disabling also snapped the software keyboard shut — so the moment
 * of successfully finishing looked exactly like the app crashing, at the
 * precise second the squeeze had everyone else typing for their lives. The
 * owner's words from the family game: the fields "become gray" and he "can't
 * tap". He was right on both counts; the state was correct and the reading
 * was "broken".
 *
 * The lock is real and server-enforced, and nothing here re-enables editing.
 * This just makes locked read as DONE: a green check where the button was
 * heading, your four words printed like answers instead of greyed-out form
 * state, and the people you are waiting on, by name. The clock stays in the
 * top bar and the squeeze stays in the status lane, so "how long" and "who's
 * left" are both answered without a single dead control on screen.
 *
 * The answer rows are the Look-Up panel's rows on purpose — "what did I put
 * for Animal again?" is the same question in both places, and it should get
 * the same furniture.
 */
function LockedInSheet({
  categories,
  answers,
  players,
  submittedPlayers,
  meId,
}: {
  categories: string[];
  answers: Record<string, string>;
  players: any[];
  submittedPlayers: Set<string>;
  meId?: string;
}) {
  const waiting = players.filter(p => p.id !== meId && !submittedPlayers.has(p.id)).map(p => p.name);
  const waitingText =
    waiting.length === 0
      ? 'Everyone’s in — tallying up…'
      : waiting.length === 1
        ? `Waiting for ${waiting[0]}…`
        : waiting.length === 2
          ? `Waiting for ${waiting[0]} and ${waiting[1]}…`
          : `Waiting for ${waiting[0]}, ${waiting[1]} and ${waiting.length - 2} more…`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="wr-col gap-3 mt-4"
      style={{ flex: 1 }}
    >
      <div
        className="wr-row gap-2"
        role="status"
        style={{ alignItems: 'center', justifyContent: 'center', color: 'var(--green)' }}
      >
        <CheckCircle2 size={18} aria-hidden />
        <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' }}>
          You’re in.
        </span>
      </div>

      <div className="wr-col gap-1">
        {categories.map(c => {
          const word = (answers[c] || '').trim();
          return (
            <div
              key={c}
              className="wr-between"
              style={{
                alignItems: 'baseline',
                padding: '12px 16px',
                borderRadius: 12,
                background: 'var(--bg-2)',
              }}
            >
              <span className="eyebrow" style={{ fontSize: 10 }}>{c}</span>
              <span
                style={{
                  fontFamily: 'var(--display)',
                  fontWeight: 700,
                  fontSize: 17,
                  color: word ? 'var(--ink-1)' : 'var(--ink-3)',
                }}
              >
                {word || '—'}
              </span>
            </div>
          );
        })}
      </div>

      <div
        aria-live="polite"
        style={{
          fontFamily: 'var(--display)',
          fontWeight: 700,
          fontSize: 13,
          color: 'var(--accent)',
          textAlign: 'center',
        }}
      >
        {waitingText}
      </div>
    </motion.div>
  );
}

/* ============================================================ */
/* LOOK UP 📺                                                    */
/* ============================================================ */

/**
 * What a non-host sees during the tally when a television is in the room.
 *
 * It covers the cascade rather than replacing it — the real one is still
 * mounted and still running underneath, at the identical beat as the TV, so
 * that unplugging the television mid-tally lifts this panel to reveal a reveal
 * already in progress rather than one restarting from the top.
 *
 * It shows the four words YOU wrote, and no scores. The scores are the joke,
 * and the joke belongs to the room, on the wall, all at once — a player reading
 * their own result off their palm three seconds early is a player who has
 * already stopped laughing by the time everyone else starts.
 */
function LookUpPanel({
  categories,
  answers,
}: {
  categories: string[];
  answers: Record<string, string>;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: 24,
      }}
    >
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        style={{ fontSize: 52 }}
      >
        📺
      </motion.div>

      <div
        style={{
          fontFamily: 'var(--display)',
          fontWeight: 900,
          fontSize: 32,
          letterSpacing: '-0.02em',
          textAlign: 'center',
        }}
      >
        Look up
      </div>
      <div className="wr-text-3" style={{ fontSize: 14, textAlign: 'center', marginTop: -12 }}>
        The tally’s on the TV.
      </div>

      {/* Your own row, small. "What did I put for Animal again?" */}
      <div className="wr-col gap-1" style={{ width: '100%', maxWidth: 320 }}>
        {categories.map(c => {
          const word = (answers?.[c] || '').trim();
          return (
            <div
              key={c}
              className="wr-between"
              style={{
                alignItems: 'baseline',
                padding: '10px 14px',
                borderRadius: 12,
                background: 'var(--bg-2)',
              }}
            >
              <span className="eyebrow" style={{ fontSize: 10 }}>{c}</span>
              <span
                style={{
                  fontFamily: 'var(--display)',
                  fontWeight: 700,
                  fontSize: 16,
                  color: word ? 'var(--ink-1)' : 'var(--ink-3)',
                }}
              >
                {word || '—'}
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/**
 * Dev-only. Extracted so the entire component — and its `devAutofill` call —
 * sits behind the `import.meta.env.DEV` branch and never reaches production.
 */
function DevAutofillButton({
  letter,
  answers,
  setAnswers,
}: {
  letter: string;
  answers: Record<string, string>;
  setAnswers: (a: Record<string, string>) => void;
}) {
  const [autofilling, setAutofilling] = useState(false);

  const handleAutofill = async () => {
    if (autofilling) return;
    setAutofilling(true);
    try {
      const filled = await websocketService.devAutofill();
      const next: Record<string, string> = { ...answers };
      for (const cat of Object.keys(filled)) {
        const value = (filled[cat] || '').trim();
        if (!value) continue;
        next[cat] = value.toUpperCase().startsWith(letter.toUpperCase())
          ? value
          : `${letter}${value}`;
      }
      setAnswers(next);
    } catch (e) {
      console.warn('autofill failed', e);
    } finally {
      setAutofilling(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleAutofill}
      disabled={autofilling}
      style={{
        alignSelf: 'flex-end',
        marginTop: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 999,
        background: 'transparent',
        border: '1px dashed var(--line-2)',
        color: 'var(--ink-3)',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'var(--ui)',
        cursor: 'pointer',
        opacity: autofilling ? 0.6 : 1,
      }}
      title="Dev only: AI-fill all categories"
    >
      {autofilling ? 'thinking…' : 'DEV: AI fill'}
    </button>
  );
}

/**
 * Two different doors behind one X.
 *
 * `canAbort` — host, round 1, nobody has written anything yet. Nothing has
 * happened, so nothing needs to be paid for: the game is called off, no result
 * recorded, everyone goes home.
 *
 * Otherwise it's a FORFEIT, and we say the word. What we must never do is what
 * the playtest build did — hide the door entirely the moment someone submitted,
 * leaving the player to kill the tab.
 */
function LeaveConfirmModal({
  open,
  canAbort,
  onCancel,
  onLeave,
}: {
  open: boolean;
  canAbort: boolean;
  onCancel: () => void;
  onLeave: () => void;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={canAbort ? 'Call off the game' : 'Leave the game'}
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="card lg"
        style={{
          width: 'calc(100% - 32px)',
          maxWidth: 440,
          margin: 16,
          background: 'var(--surface)',
          padding: 22,
        }}
      >
        <div className="h3">{canAbort ? 'Call off the game?' : "Leave the game? You'll forfeit."}</div>
        <p className="wr-text-3" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.45 }}>
          {canAbort
            ? "Nobody's written anything yet, so nothing gets recorded. Everyone goes back home."
            : 'The round has started. If you go now it counts as a forfeit — the others keep playing without you.'}
        </p>
        <div className="wr-col gap-2 mt-4">
          <Btn
            kind="dark"
            full
            icon={canAbort ? 'X' : 'House'}
            onClick={onLeave}
            style={canAbort ? undefined : { color: 'var(--coral, #b94a3b)' }}
          >
            {canAbort ? 'Call it off' : 'Leave and forfeit'}
          </Btn>
          <Btn kind="ghost" full onClick={onCancel}>
            Stay in the game
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* PHASE 5 — Final winner (full-screen takeover)                 */
/* ============================================================ */

/**
 * The biggest moment of the game used to happen inside a 460px box floating
 * over a dimmed screen — with the Play again / Home / Share buttons clipped
 * off the bottom. It's a full-screen takeover now, and the action bar is
 * pinned so it cannot clip at any height (verified against 360×640).
 */
function FinalPhase({
  finalResults,
  players,
  totalRounds,
  shareGameId,
  onPlayAgain,
  onHome,
  onSignIn,
}: {
  finalResults: GameEndedPayload;
  players: any[];
  totalRounds: number;
  /** Where the share card lives. Null until the server has written the record. */
  shareGameId: string | null;
  onPlayAgain: () => void;
  onHome: () => void;
  onSignIn?: () => void;
}) {
  const { currentUser } = useAuth();
  const isGuest = !currentUser;
  const desktop = useSurface() === 'desktop';
  const kb = useKeyboardSurface();

  /**
   * ⏎ = play again. Esc = home.
   *
   * The two things anybody actually wants at the end of a game, on the two
   * keys everybody already knows. `useHotkeys` refuses to fire inside a
   * dialog, so the share sheet can't be hijacked by them.
   */
  useHotkeys(
    [
      { keys: 'Enter', handler: onPlayAgain },
      { keys: 'Escape', handler: onHome },
    ],
    kb,
  );

  const results: GameResultPlayer[] = useMemo(() => {
    // Backend emits `players` (ranked list); legacy callers used `results`.
    const ranking = finalResults.players || finalResults.results;
    if (ranking && ranking.length) return ranking;
    return [...players]
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0))
      .map((p: any, i: number) => ({
        playerId: p.id,
        playerName: p.name,
        playerAvatar: p.avatar,
        totalScore: p.score || 0,
        position: i + 1,
      }));
  }, [finalResults, players]);

  const winner = results[0];
  const me = useMemo(() => {
    if (!currentUser) return null;
    return results.find(r => r.playerId === currentUser.uid) || null;
  }, [results, currentUser]);
  const youWon = !!(me && winner && me.playerId === winner.playerId);

  /* ---------- The winner ----------
   * ONE warm chord. Once per GAME, never per round, and not a fanfare.
   *
   * This is the sound that most needs the one-room rule. Six phones playing a
   * 1.2s pitched chord on the same frame is a smear — so each device offsets
   * itself by hash(its own player id) % 250ms and the room STRUMS instead. The
   * hash is stable, so no two phones pick the same slot by accident and nobody
   * needs to coordinate.
   *
   * The winner also gets the [10,40,10] haptic — the only pattern in the game.
   */
  const chordedRef = useRef(false);
  useEffect(() => {
    if (chordedRef.current || !results.length) return;
    chordedRef.current = true;
    soundService.chord(currentUser?.uid || me?.playerId);
  }, [results, currentUser, me]);

  return (
    <div
      className="app-shell"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        background: 'linear-gradient(180deg, var(--accent-soft) 0%, var(--bg) 42%)',
        display: 'flex',
        flexDirection: 'column',
        // Never let the takeover run under a notch or a home indicator.
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Twice the pieces on a canvas three times the size. Same burst, same
          one-shot, same 1.8s reveal — it fires ONCE, at game end, never at
          round end. */}
      <Confetti count={desktop ? 60 * STAGE.CONFETTI_MULT_LG : 60} />

      {/* Scrollable body. The action bar below is OUTSIDE this, so it can
          never be pushed off-screen no matter how many players there are. */}
      <div
        className="wr-final-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '20px 20px 8px',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div className="wr-col wr-center gap-3">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Pill kind="accent" icon="Trophy">Game over</Pill>
          </motion.div>

          {winner && (
            <>
              <motion.div
                initial={{ opacity: 0, scale: 0.4, rotate: -12 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 220, damping: 12 }}
              >
                <Avatar id={winner.playerAvatar} size={104} />
              </motion.div>
              <motion.div
                className="h2"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                style={{ marginTop: 2, textAlign: 'center' }}
              >
                {youWon ? 'You won!' : `${winner.playerName} wins!`}
              </motion.div>
              <motion.div
                className="wr-final-score"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 }}
                style={{
                  fontFamily: 'var(--display)',
                  fontWeight: 800,
                  fontSize: 56,
                  letterSpacing: '-0.04em',
                  color: 'var(--accent)',
                  lineHeight: 1,
                }}
              >
                {winner.totalScore}
              </motion.div>
              <div className="eyebrow">points · {totalRounds} rounds</div>

              {/* Your line — the signed-in player's own score, even when they
                  didn't win. Without it, you come off a 5-round game and see
                  one big number that isn't yours. */}
              {me && !youWon && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.85 }}
                  className="card"
                  style={{
                    marginTop: 6,
                    padding: '10px 14px',
                    background: 'var(--surface)',
                    boxShadow: 'var(--shadow-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    maxWidth: 420,
                  }}
                >
                  <Avatar id={me.playerAvatar} size={28} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>You scored</span>
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22, marginLeft: 'auto' }}>{me.totalScore}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· #{me.position}</span>
                </motion.div>
              )}
            </>
          )}
        </div>

        <div className="wr-col gap-2 mt-4" style={{ maxWidth: 460, margin: '16px auto 0' }}>
          {results.map((r, i) => (
            <motion.div
              key={r.playerId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(1.0 + i * 0.06, 1.6) }}
              className="prow"
              style={{ padding: '10px 14px' }}
            >
              <span
                style={{
                  fontFamily: 'var(--display)',
                  fontWeight: 700,
                  fontSize: 16,
                  width: 24,
                  color:
                    i === 0 ? 'var(--gold)'
                      : i === 1 ? 'var(--silver)'
                        : i === 2 ? 'var(--bronze)'
                          : 'var(--ink-3)',
                }}
              >
                {i + 1}
              </span>
              <Avatar id={r.playerAvatar} size={32} />
              <div className="body" style={{ minWidth: 0 }}>
                <div className="name" style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.playerName}
                </div>
              </div>
              <span className="score sm">{r.totalScore}</span>
            </motion.div>
          ))}
        </div>

        {/*
          THE CARD. Not behind a button — the memo is explicit that any friction
          between the laugh and the send kills the send, so it composes itself
          here, on the results bridge, while the table is still arguing about
          whether a nose is an animal.
        */}
        <div style={{ marginTop: 18 }}>
          <ShareSheet
            gameId={shareGameId}
            caption="Everyone at the table can send this."
          />
        </div>

        {/* "Keep this table?" — the only door into the Table primitive. It
            renders itself to nothing unless this was a multiplayer night whose
            roster isn't already a table, so there is no condition to maintain
            here. See components/wr/KeepTableCard.tsx. */}
        <KeepTableCard
          gameId={shareGameId}
          players={results}
          isSolo={finalResults.gameMode === 'solo'}
        />

        {isGuest && onSignIn && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            className="card"
            style={{
              background: 'var(--accent-soft)',
              boxShadow: 'none',
              padding: 14,
              marginTop: 16,
              maxWidth: 460,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <div className="wr-row gap-3">
              <span style={{ fontSize: 20 }}>☁️</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Save this win to your history</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sign in to keep your stats and achievements.</div>
              </div>
              <Btn kind="primary" onClick={onSignIn} icon="ArrowRight">Sign in</Btn>
            </div>
          </motion.div>
        )}
      </div>

      {/* Pinned action bar. This is the fix for the clipped buttons: it lives
          outside the scroll container, so it is always on screen. */}
      <div
        style={{
          flexShrink: 0,
          padding: '10px 20px 16px',
          background: 'linear-gradient(180deg, transparent 0%, var(--bg) 34%)',
          position: 'relative',
          zIndex: 3,
        }}
      >
        <div className="wr-col gap-2" style={{ maxWidth: desktop ? 560 : 460, margin: '0 auto' }}>
          {/* Keycaps only where a keyboard exists. On a phone these are the
              identical plain-string labels they have always been. */}
          <Btn kind="primary" size="xl" full icon="RefreshCw" onClick={onPlayAgain}>
            {kb ? (
              <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
                Play again <Kbd onAccent>⏎</Kbd>
              </span>
            ) : 'Play again'}
          </Btn>
          <Btn kind="ghost" full icon="House" onClick={onHome}>
            {kb ? (
              <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
                Home <Kbd>Esc</Kbd>
              </span>
            ) : 'Home'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
